// A workflow's input fields, built from its parameters (taken out of the
// Run dialog, ./RunParamsDialog, so Try it on a deployment's start endpoint
// uses the same form): what the operator must decide shown open, each with
// its description beside its label; settings with a default folded under
// "Settings", whose line says their current values; a setting that names
// where the data is offers the connected data sources (with Connections on
// named as their connections are: useSourceOptions). Inputs a deployment
// FIXES are shown read-only with their value; `only` limits the form to the
// inputs a caller may set. The state (the form, its problems) stays with
// the caller: ../model initialRunForm / runFormProblems / runParamsBody.

import { useMemo, useState } from "react";

import { useConnectionsSwitch } from "@/api/connections";
import { useConnectionsDirectory } from "@/api/connectionsDirectory";
import { useDataSources } from "@/api/dataSources";
import type { ParameterSpec } from "@/api/workflows";
import { Disclosure } from "@/components/ui/Disclosure";
import { Dropdown } from "@/components/ui/Dropdown";
import { Input, Textarea } from "@/components/ui/Input";
import { Switch } from "@/components/ui/Switch";
import { TokenInput } from "@/components/ui/TokenInput";
import { sourceTitles } from "@/features/connections/directoryVendors";
import { BlueprintField as Field } from "@/features/subagents/blueprints/fields";

import { MARKET_DATA, MARKET_DATA_LABEL, choiceLabel, isSourceParam, paramHelp, runFormGroups, settingsSummary } from "../list/runFormModel";
import type { RunForm, RunFormValue } from "../model";

/**
 * The workspace's connected data sources as the "where the data is" choice
 * offers them; with Connections on each named as its connection is
 * (directoryVendors.sourceTitles).
 */
export function useSourceOptions(customerId: string | null | undefined): SourceOption[] {
  const sourcesQuery = useDataSources(customerId || null);
  const on = useConnectionsSwitch(customerId || null) === true;
  const dir = useConnectionsDirectory(on ? customerId || null : null);
  const entries = on ? (dir.data?.entries ?? null) : null;
  return useMemo(() => {
    const list = sourcesQuery.data?.sources ?? [];
    return entries ? sourceTitles(list, entries) : list.map((s) => ({ name: s.name, title: s.title || s.name }));
  }, [sourcesQuery.data, entries]);
}

/** A fixed input's value as words ("2026-10-28", "SPY, QQQ", "Yes"). */
export function fixedWords(value: unknown): string {
  if (value === null || value === undefined || value === "") return "None";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map(String).join(", ");
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

export interface ParamFieldsProps {
  params: readonly ParameterSpec[];
  form: RunForm;
  onChange: (name: string, value: RunFormValue) => void;
  /** Each field's problem (../model runFormProblems), shown once `showProblems`. */
  problems: Record<string, string>;
  showProblems: boolean;
  sources: readonly SourceOption[];
  /** Inputs fixed by a deployment: shown read-only with this value, never edited. */
  fixed?: Record<string, unknown>;
  /** Only these inputs (a start endpoint's exposed ones); every one when absent. */
  only?: readonly string[];
  /** The "Settings" fold, controlled (open when a problem hides in it); uncontrolled when absent. */
  foldOpen?: boolean | null;
  onFoldOpenChange?: (open: boolean) => void;
}

export function ParamFields({ params, form, onChange, problems, showProblems, sources, fixed, only, foldOpen, onFoldOpenChange }: ParamFieldsProps) {
  const fixedNames = new Set(Object.keys(fixed ?? {}));
  const shown = params.filter((p) => fixedNames.has(p.name) || !only || only.includes(p.name));
  const editable = shown.filter((p) => !fixedNames.has(p.name));
  const pinned = shown.filter((p) => fixedNames.has(p.name));
  const { open, folded } = runFormGroups(editable);
  const [ownOpen, setOwnOpen] = useState<boolean | null>(null);
  const isOpen = foldOpen ?? ownOpen;
  const sourceTitle = (n: string) => sources.find((s) => s.name === n)?.title ?? null;

  const field = (p: ParameterSpec) => (
    <Field key={p.name} label={`${p.label || p.name}${p.required ? " (required)" : ""}`} hint={paramHelp(p) || undefined} error={showProblems ? problems[p.name] : null}>
      {({ id }) => <ParamControl id={id} param={p} value={form[p.name]} sources={sources} onChange={(v) => onChange(p.name, v)} />}
    </Field>
  );
  const fixedField = (p: ParameterSpec) => (
    <Field key={p.name} label={`${p.label || p.name} (fixed)`} hint={paramHelp(p) || undefined}>
      {({ id }) => <Input id={id} readOnly value={fixedWords(fixed?.[p.name])} />}
    </Field>
  );

  return (
    <div className="flex flex-col gap-4">
      {open.map(field)}
      {pinned.map(fixedField)}
      {folded.length > 0 && (
        <Disclosure
          summary="Settings"
          count={folded.length}
          meta={settingsSummary(folded, form, sourceTitle)}
          open={isOpen ?? open.length === 0}
          onOpenChange={(o) => (onFoldOpenChange ? onFoldOpenChange(o) : setOwnOpen(o))}
        >
          <div className="flex flex-col gap-4 pt-2">{folded.map(field)}</div>
        </Disclosure>
      )}
    </div>
  );
}

/** A connected data source as the "where the data is" choice offers it. */
export interface SourceOption {
  name: string;
  title: string;
}

const OTHER = "__other__";

interface ParamControlProps {
  id: string;
  describedBy?: string;
  param: ParameterSpec;
  value: RunFormValue | undefined;
  sources: readonly SourceOption[];
  onChange: (v: RunFormValue) => void;
}

/** One parameter's control, by its type (the Run dialog's; the Run settings card uses it for a scheduled run's values). */
export function ParamControl({ id, describedBy, param: p, value, sources, onChange }: ParamControlProps) {
  const text = typeof value === "string" ? value : "";
  switch (p.type) {
    case "list":
      return (
        <TokenInput
          ariaLabel={p.label || p.name}
          values={Array.isArray(value) ? value : []}
          onChange={onChange}
          placeholder={p.choices?.length ? `One of: ${p.choices.map((c) => choiceLabel(p.choices!, c)).join(", ")}` : "Add a value"}
          validate={p.choices?.length ? (v) => p.choices!.includes(v) || `Not one of ${p.choices!.join(", ")}` : undefined}
        />
      );
    case "boolean":
      return <Switch id={id} checked={value === true} onChange={onChange} label={value === true ? "Yes" : "No"} />;
    case "date":
      return <Input id={id} type="date" aria-describedby={describedBy} value={text} onChange={(e) => onChange(e.target.value)} />;
    case "number":
    case "integer":
      return (
        <Input
          id={id}
          type="number"
          aria-describedby={describedBy}
          inputMode={p.type === "integer" ? "numeric" : "decimal"}
          step={p.type === "integer" ? 1 : "any"}
          value={text}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case "text":
      return <Textarea id={id} rows={4} aria-describedby={describedBy} value={text} onChange={(e) => onChange(e.target.value)} />;
    default:
      if (p.choices?.length) {
        return (
          <Dropdown
            id={id}
            ariaLabel={p.label || p.name}
            className="w-full"
            matchTriggerWidth
            value={text}
            placeholder="Choose"
            options={[...(p.required ? [] : [{ value: "", label: "None" }]), ...p.choices.map((c) => ({ value: c, label: choiceLabel(p.choices!, c) }))]}
            onChange={onChange}
          />
        );
      }
      if (isSourceParam(p, value, sources.map((s) => s.name))) {
        return <SourceControl id={id} describedBy={describedBy} param={p} value={text} sources={sources} onChange={onChange} />;
      }
      return <Input id={id} aria-describedby={describedBy} value={text} onChange={(e) => onChange(e.target.value)} />;
  }
}

/** Where the data is: a connected source, the market-data feed, or something else typed in. */
function SourceControl({ id, describedBy, param: p, value, sources, onChange }: Omit<ParamControlProps, "value"> & { value: string }) {
  const known = value === MARKET_DATA || sources.some((s) => s.name === value);
  const [other, setOther] = useState(!known && value !== "");
  const choice = other || (!known && value !== "") ? OTHER : value;
  return (
    <div className="flex flex-col gap-2">
      <Dropdown
        id={id}
        ariaLabel={p.label || p.name}
        className="w-full"
        matchTriggerWidth
        value={choice}
        placeholder="Choose"
        options={[...sources.map((s) => ({ value: s.name, label: s.title })), { value: MARKET_DATA, label: MARKET_DATA_LABEL }, { value: OTHER, label: "Something else…" }]}
        onChange={(v) => {
          setOther(v === OTHER);
          if (v !== OTHER) onChange(v);
          else if (known) onChange("");
        }}
      />
      {choice === OTHER && (
        <Input
          aria-label={`${p.label || p.name}: something else`}
          aria-describedby={describedBy}
          autoFocus
          placeholder="A file name, a project file or a web address"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
